import React from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/layout/Header';
import { FiArrowLeft, FiExternalLink, FiMusic, FiMoon, FiSun } from 'react-icons/fi';
import { GiMeditation } from 'react-icons/gi';

const CalmPage: React.FC = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gray-100">
      <Header />

      <main className="max-w-7xl mx-auto px-4 py-6">
        {/* Navigation */}
        <div className="mb-6">
          <button
            onClick={() => navigate('/home')}
            className="flex items-center text-uc-navy hover:text-uc-gold transition-colors mb-4"
          >
            <FiArrowLeft size={20} className="mr-2" />
            Back to Home
          </button>
          <h1 className="text-3xl font-bold text-uc-navy font-header">CALM</h1>
          <p className="text-gray-600 mt-2">Meditation and mental wellness</p>
        </div>

        {/* Hero Section */}
        <div className="bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg shadow p-8 mb-6 text-white">
          <div className="flex items-center gap-4 mb-4">
            <GiMeditation size={48} />
            <div>
              <h2 className="text-3xl font-bold">Welcome to Calm</h2>
              <p className="text-blue-100">Your mental wellness companion</p>
            </div>
          </div>
          <p className="mb-6">
            UC Merced provides free access to Calm for all students. Reduce stress, improve sleep,
            and find moments of peace with guided meditations, sleep stories, and relaxation exercises.
          </p>
          <a
            href="https://www.calm.com/schools"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-white text-blue-600 px-6 py-3 rounded-lg hover:bg-blue-50 transition-colors font-semibold"
          >
            Access Calm <FiExternalLink size={18} />
          </a>
        </div>

        {/* Features */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-4">
              <GiMeditation className="text-blue-600" size={32} />
            </div>
            <h3 className="font-bold text-uc-navy mb-2">Guided Meditations</h3>
            <p className="text-gray-600 text-sm">
              Reduce stress and anxiety with expert-led meditation sessions ranging from 3 to 30 minutes.
            </p>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mb-4">
              <FiMoon className="text-purple-600" size={32} />
            </div>
            <h3 className="font-bold text-uc-navy mb-2">Sleep Stories</h3>
            <p className="text-gray-600 text-sm">
              Fall asleep faster with relaxing bedtime stories narrated by soothing voices.
            </p>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
              <FiMusic className="text-green-600" size={32} />
            </div>
            <h3 className="font-bold text-uc-navy mb-2">Relaxing Music</h3>
            <p className="text-gray-600 text-sm">
              Focus better or unwind with curated playlists and ambient soundscapes.
            </p>
          </div>
        </div>

        {/* How to Access */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h2 className="text-xl font-bold text-uc-navy mb-4">How to Access Calm</h2>
          <ol className="space-y-3 text-gray-700">
            <li className="flex items-start gap-3">
              <span className="flex-shrink-0 w-6 h-6 bg-uc-gold text-white rounded-full flex items-center justify-center text-sm font-bold">1</span>
              <p>Visit <a href="https://www.calm.com/schools" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">calm.com/schools</a></p>
            </li>
            <li className="flex items-start gap-3">
              <span className="flex-shrink-0 w-6 h-6 bg-uc-gold text-white rounded-full flex items-center justify-center text-sm font-bold">2</span>
              <p>Sign up using your UC Merced email address (@ucmerced.edu)</p>
            </li>
            <li className="flex items-start gap-3">
              <span className="flex-shrink-0 w-6 h-6 bg-uc-gold text-white rounded-full flex items-center justify-center text-sm font-bold">3</span>
              <p>Verify your email and create your free account</p>
            </li>
            <li className="flex items-start gap-3">
              <span className="flex-shrink-0 w-6 h-6 bg-uc-gold text-white rounded-full flex items-center justify-center text-sm font-bold">4</span>
              <p>Download the Calm app on iOS or Android for on-the-go access</p>
            </li>
          </ol>
        </div>

        {/* Mental Health Resources */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-6">
          <div className="flex items-start gap-3">
            <FiSun className="text-blue-600 mt-1 flex-shrink-0" size={24} />
            <div>
              <h3 className="font-bold text-blue-900 mb-2">Additional Mental Health Resources</h3>
              <p className="text-blue-800 mb-3">
                If you're experiencing a mental health crisis or need additional support:
              </p>
              <ul className="space-y-2 text-sm text-blue-800">
                <li>• Counseling & Psychological Services (CAPS): (209) 228-4266</li>
                <li>• National Suicide Prevention Lifeline: 988</li>
                <li>• Crisis Text Line: Text "HOME" to 741741</li>
                <li>• Student Health Center: (209) 228-2273</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Info Section */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="font-bold text-uc-navy mb-4">About Calm at UC Merced</h3>
          <div className="space-y-3 text-gray-700">
            <p>
              UC Merced partners with Calm to provide free access to premium mental wellness resources.
              Research shows that regular meditation and mindfulness practice can:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Reduce stress and anxiety</li>
              <li>Improve focus and concentration</li>
              <li>Enhance sleep quality</li>
              <li>Boost emotional well-being</li>
              <li>Increase self-awareness</li>
              <li>Support overall mental health</li>
            </ul>
            <p className="text-sm text-gray-600 mt-4">
              For questions about Calm access, contact Student Affairs or visit the Counseling Center.
            </p>
          </div>
        </div>
      </main>
    </div>
  );
};

export default CalmPage;
